import React, { useContext, useEffect, useState, useCallback } from 'react';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * MovimientosBancos — SOLO LECTURA (el backend `movimientos-bancos.routes.js` no
 * expone POST/PATCH/DELETE; otros módulos crean sus movimientos directamente). No usa
 * `useListado`/`ResourcePage` porque los filtros reales son SEGMENTOS DE RUTA
 * (`/movimientos-bancos/fechas/:inicio/:fin`, `/movimientos-bancos/banco/:banco`), no
 * query params de paginación — confirmado contra `movimientos-bancos.routes.js`.
 *
 * Sort: gap cerrado en la auditoría 2026-09-28 (V1
 * `ListadomovimientosBancos.jsx#headers/commentsData` ya tenía encabezados
 * ordenables por click, ademas del buscador que esta pagina ya tenia) — se agrega
 * orden 100% en cliente (mismo criterio que V1: el endpoint regresa el arreglo
 * completo, sin paginación real de servidor).
 */
function hoyISO() {
  return new Date().toISOString().slice(0, 10);
}
function hace30DiasISO() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

const POR_PAGINA = 50; // V1: ITEMS_PER_PAGE = 50
const fmtUSD = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(n || 0));
const fmtFecha = (f) => { // V1 `formatDate`: YYYY-MM-DD -> DD-MM-YYYY
  const p = String(f || '').split('-');
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : (f || '');
};

export default function MovimientosBancosPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('movimientos-bancos:ver');

  const [inicio, setInicio] = useState(hace30DiasISO());
  const [fin, setFin] = useState(hoyISO());
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [mailOpen, setMailOpen] = useState(false);

  function ordenarPor(campo) {
    setPagina(1);
    if (campo === sortBy) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(campo); setSortOrder('asc'); }
  }

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`/movimientos-bancos/fechas/${inicio}/${fin}`);
      setFilas(Array.isArray(data.data) ? data.data : data.data?.data || []);
    } catch (e) {
      setError(e.message ?? 'No se pudo cargar la información');
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, [inicio, fin]);

  useEffect(() => { cargar(); }, [cargar]);

  const bancoNombre = (r) => (typeof r.banco === 'string' ? r.banco : r.banco?.banco || '');
  const importeDe = (r) => Number(r.importe ?? (Number(r.abono || 0) - Number(r.cargo || 0)) ?? 0);

  // Buscador igual que V1: movimiento, observaciones, banco, moneda e importe.
  const s = busqueda.toLowerCase();
  const filasFiltradas = busqueda
    ? filas.filter((f) =>
        String(f.movimiento ?? '').toLowerCase().includes(s)
        || String(f.observaciones ?? '').toLowerCase().includes(s)
        || bancoNombre(f).toLowerCase().includes(s)
        || String(f.moneda ?? '').toLowerCase().includes(s)
        || String(importeDe(f)).includes(busqueda))
    : filas;

  const filasOrdenadas = !sortBy ? filasFiltradas : [...filasFiltradas].sort((a, b) => {
    let av; let bv;
    if (sortBy === 'banco') { av = bancoNombre(a); bv = bancoNombre(b); }
    else if (sortBy === 'importe') { av = importeDe(a); bv = importeDe(b); }
    else { av = a[sortBy]; bv = b[sortBy]; }
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av ?? '').localeCompare(String(bv ?? ''));
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  const totalMXN = filasFiltradas.filter((f) => f.moneda === 'MXN').reduce((t, f) => t + importeDe(f), 0);
  const totalUSD = filasFiltradas.filter((f) => f.moneda === 'USD').reduce((t, f) => t + importeDe(f), 0);

  const totalPaginas = Math.max(1, Math.ceil(filasOrdenadas.length / POR_PAGINA));
  const paginaActual = Math.min(pagina, totalPaginas);
  const filasPagina = filasOrdenadas.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA);

  // Columnas réplica 1:1 del <tbody> de V1 (`ListadomovimientosBancos.jsx`): Fecha,
  // Movimiento, Banco, Destino (= observaciones), Importe, Moneda.
  const columnas = [
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => fmtFecha(r.fecha) },
    { key: 'movimiento', label: 'Movimiento', sortable: true },
    { key: 'banco', label: 'Banco', sortable: true, render: (r) => bancoNombre(r) || '—' },
    { key: 'observaciones', label: 'Destino', sortable: true },
    { key: 'importe', label: 'Importe', className: 'text-right', sortable: true, render: (r) => fmtUSD(importeDe(r)) },
    { key: 'moneda', label: 'Moneda', sortable: true },
  ];
  // PDF/Excel de V1: Fecha, Movimiento, Observaciones, Importe, Moneda (sin Banco).
  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'movimiento', label: 'Movimiento' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'importe', label: 'Importe', render: (r) => fmtUSD(importeDe(r)) },
    { key: 'moneda', label: 'Moneda' },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">Movimientos Bancos</h1>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={inicio} onChange={(e) => { setPagina(1); setInicio(e.target.value); }} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={fin} onChange={(e) => { setPagina(1); setFin(e.target.value); }} className="input-base" />
        </div>
        <input
          value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }} placeholder="Buscar..."
          className="input-base w-56"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => exportTablePdf('Detalle de Movimientos', columnasExport, filasOrdenadas, { fileName: 'DetalleMovimientos' })}
            className="btn-outline text-xs px-3 py-1.5"
          >
            PDF
          </button>
          <button type="button" onClick={() => setMailOpen(true)} className="btn-outline text-xs px-3 py-1.5">
            eMail
          </button>
          <button
            type="button"
            onClick={() => exportTableExcel('Detalle de Movimientos', columnasExport, filasOrdenadas, { fileName: `DetalleMovimientos-${inicio}-${fin}`, sheetName: 'DetalleMovimientos' })}
            className="btn-outline text-xs px-3 py-1.5"
          >
            Excel
          </button>
        </div>
        <h4 className="ml-auto text-base font-semibold text-slate-900">
          TOTAL {fmtUSD(totalUSD)} USD / {fmtUSD(totalMXN)} MXN
        </h4>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable
        columns={columnas} data={filasPagina} loading={cargando} empty="Sin movimientos en el rango seleccionado"
        sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        rowKey={(row, i) => `${row.origenId}-${row.tipo}-${row.numero}-${i}`}
      />
      <Pagination pagination={{ totalDocs: filasOrdenadas.length, totalPages: totalPaginas }} page={paginaActual} onChange={setPagina} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Movimientos"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Detalle de Movimientos', columnasExport, filasOrdenadas, { fileName: 'DetalleMovimientos' });
          await enviarCorreoAdjunto({
            subject: 'Lista de Movimientos', email, fileName: 'ListaMovimientos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras la lista de Movimientos.'),
          });
        }}
      />
    </div>
  );
}
